import type { ReactNode } from 'react';
import { EmergeLockup } from '@/components/EmergeLockup';
import { Panel, PanelBody } from '@/components/ui';
import { BrandPanel } from './BrandPanel';

interface AuthLayoutProps {
  title: string;
  subtitle?: ReactNode;
  /**
   * Keep the heading for assistive technology and take it off the screen.
   *
   * Set on `/login` alone, where the lockup directly above already says SEED
   * EMERGE and a visible "Sign in to SEED EMERGE" only repeated it. The other
   * six callers — the forgot-password and reset-password states — carry real
   * information in their titles and must keep them visible.
   *
   * ⚠️ THE `<h1>` STAYS IN THE DOCUMENT, and that is the point of the flag
   * rather than dropping the prop. A page with no heading has no outline for a
   * screen reader to navigate by, and the lockup cannot stand in for one: it is
   * an image whose accessible name is the product, not the page. Removing the
   * element outright is a different change from hiding it, and only one of them
   * is what "take these two lines off the screen" asked for.
   */
  hideHeading?: boolean;
  children: ReactNode;
}

/**
 * The signed-out shell: a sign-in card beside a brand panel on large screens.
 *
 * This renders the page's `<main>`, so routes must not add another one. The
 * brand panel is declared after the card and pulled left with `lg:order-first`
 * so the form comes first in reading order.
 */
export function AuthLayout({ title, subtitle, hideHeading, children }: AuthLayoutProps) {
  const year = new Date().getFullYear();

  return (
    <div className="grid min-h-svh w-full bg-canvas text-fg lg:grid-cols-2">
      <main className="flex items-center justify-center px-4 py-10 sm:px-6 sm:py-12">
        <div className="w-full max-w-[400px]">
          <Panel>
            <PanelBody density="comfortable">
              {/*
               * Below `lg` only. From `lg` the brand panel beside this card
               * carries the lockup, and drawing it in both places put the
               * signature on screen twice. The top margins below drop at `lg`
               * for the same reason: nothing sits above them there.
               */}
              <header className="lg:hidden">
                <EmergeLockup className="w-[196px] max-w-full" />
              </header>

              {hideHeading ? (
                <h1 className="sr-only">{title}</h1>
              ) : (
                <div className="mt-6 lg:mt-0">
                  <h1 className="m-0 text-xl font-semibold tracking-[-0.01em] text-fg">{title}</h1>
                  {subtitle && <p className="mt-1.5 text-sm text-fg-muted">{subtitle}</p>}
                </div>
              )}

              <div className={hideHeading ? 'mt-6 lg:mt-0' : 'mt-6'}>{children}</div>
            </PanelBody>
          </Panel>

          <p className="mt-4 text-center text-xs text-fg-subtle">
            © {year} SEED Global Education. All rights reserved.
          </p>
        </div>
      </main>

      <BrandPanel />
    </div>
  );
}
